import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useCart } from '../../../context/CartContext';
import apiManager from '../../../lib/api';
import { Product } from '../../../lib/api/models';
import SideMenu from './side-menu';
import { T, useTranslate } from '@tolgee/react';
import { OptimizedImage } from '../../../components/ui/OptimizedImage';
import { getOperatorBasePath, getOperatorPanelLabel } from '../../commercial/utils/operator';

const normalizeRef = (value: string) => value.trim().toUpperCase();

const isReferenceLikeQuery = (value: string) => {
  const normalized = normalizeRef(value);
  return normalized.length >= 4 && /\d/.test(normalized) && /^[A-Z0-9._/-]+$/.test(normalized);
};

const getProductId = (product: Product) => String(product.id || (product as any)._id || '');

const productMatchesRef = (product: Product, query: string) => {
  const normalizedQuery = normalizeRef(query);
  if (!normalizedQuery) return false;

  if (normalizeRef(product.sku || '') === normalizedQuery) {
    return true;
  }

  return Array.isArray(product.variants)
    && product.variants.some(variant => normalizeRef(variant.sku || '') === normalizedQuery);
};

const prioritizeExactRefMatch = (products: Product[], query: string) => {
  if (!isReferenceLikeQuery(query)) return products;

  const exactMatch = products.find(product => productMatchesRef(product, query));
  if (!exactMatch) return products;

  const exactId = getProductId(exactMatch);
  return [
    exactMatch,
    ...products.filter(product => getProductId(product) !== exactId)
  ];
};

const lookupExactProduct = async (query: string, products: Product[] = []) => {
  if (!isReferenceLikeQuery(query)) return null;

  const existingMatch = products.find(product => productMatchesRef(product, query));
  if (existingMatch) return existingMatch;

  const lookup = await apiManager.products.lookupByRef(query.trim());
  const productId = lookup.success && lookup.data?.id ? String(lookup.data.id) : '';
  if (!productId) return null;

  const productResponse = await apiManager.products.getProduct(productId);
  return productResponse.success && productResponse.data ? productResponse.data : null;
};

const SearchBar = () => {
  const { t } = useTranslate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Product[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Efecto para cerrar el menú de resultados al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Función para buscar productos usando MeiliSearch
  const searchProducts = async (searchQuery: string) => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiManager.search.autocomplete(searchQuery, 5);
      let nextResults = response.success && response.data ? response.data : [];
      const exactProduct = await lookupExactProduct(searchQuery, nextResults);
      if (exactProduct) {
        const exactId = getProductId(exactProduct);
        nextResults = [
          exactProduct,
          ...nextResults.filter(product => getProductId(product) !== exactId)
        ];
      } else {
        nextResults = prioritizeExactRefMatch(nextResults, searchQuery);
      }
      setResults(nextResults);
    } catch (error) {
      console.error('Error al buscar productos:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Efecto para realizar búsqueda con debounce
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (query.trim().length >= 2) {
        searchProducts(query);
      } else {
        setResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [query]);

  const handleSubmit = async (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault();
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;

    if (isReferenceLikeQuery(trimmedQuery)) {
      try {
        const res = await apiManager.products.lookupByRef(trimmedQuery);
        if (res.success && res.data?.id) {
          const skuParam = res.data.variantSku ? `?sku=${encodeURIComponent(res.data.variantSku)}` : '';
          setShowResults(false);
          setQuery('');
          navigate(`/products/${res.data.id}${skuParam}`);
          return;
        }
      } catch {
        // fallback to local exact match or normal search
      }
    }

    const exactProduct = await lookupExactProduct(trimmedQuery, results);
    setShowResults(false);
    if (exactProduct) {
      setQuery('');
      navigate(`/products/${getProductId(exactProduct)}`);
      return;
    }

    navigate(`/products?q=${encodeURIComponent(trimmedQuery)}`);
  };

  const handleResultClick = (productId: string) => {
    setShowResults(false);
    setQuery('');
    navigate(`/products/${productId}`);
  };

  return (
    <div className="relative w-48" ref={searchContainerRef} data-testid="search-bar">
      <form onSubmit={handleSubmit} className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowResults(true);
          }}
          onFocus={() => query.trim() && setShowResults(true)}
          placeholder={t('header.search_placeholder', 'Buscar...')}
          className="w-full bg-white border border-gray-200 rounded-full py-1.5 pl-8 pr-3 text-sm focus:outline-none focus:border-gray-400"
          style={{color: '#0d0d0d'}}
          aria-label="Campo de búsqueda"
          data-testid="search-input"
        />
        <div className="absolute left-2.5 top-1/2 transform -translate-y-1/2" style={{color: '#666'}}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            className="h-3.5 w-3.5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </form>

      {/* Resultados de búsqueda */}
      {showResults && query.trim().length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-md shadow-lg z-50 max-h-96 overflow-y-auto" data-testid="search-results">
          {isLoading ? (
            <div className="p-4 text-center text-gray-500" data-testid="search-loading">
              <div className="animate-spin h-5 w-5 border-2 border-black border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-1 text-xs"><T keyName="header.search_loading">Buscando...</T></p>
            </div>
          ) : results.length > 0 ? (
            <ul>
              {results.map((product) => (
                <li key={product.id} className="border-b border-gray-100 last:border-none">
                  <button
                    onClick={() => handleResultClick(product.id)}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-3"
                    data-testid="search-product-result"
                  >
                    {product.images && product.images[0] ? (
                      <OptimizedImage
                        src={product.images[0].url}
                        alt={product.name}
                        width={40}
                        height={40}
                        type="thumbnail"
                        className="h-10 w-10 object-contain rounded bg-gray-50"
                      />
                    ) : (
                      <div className="h-10 w-10 bg-gray-100 rounded flex items-center justify-center">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                      {product.brand ? (
                        <p className="text-xs text-gray-500 truncate">{product.brand}</p>
                      ) : null}
                    </div>
                    {product.price ? (
                      <div className="text-sm font-semibold text-gray-900">
                        {product.price.toFixed(2)}€
                      </div>
                    ) : null}
                  </button>
                </li>
              ))}
              <li className="px-4 py-3 text-center border-t border-gray-200">
                <button
                  onClick={handleSubmit}
                  className="text-sm text-black hover:underline font-medium"
                  data-testid="search-view-all-button"
                >
                  <T keyName="header.search_view_all">Ver todos los resultados</T> para "{query}"
                </button>
              </li>
            </ul>
          ) : (
            <div className="p-4 text-center text-gray-500" data-testid="search-no-results">
              <p className="text-sm"><T keyName="header.search_no_results">No se encontraron productos</T></p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Componente de búsqueda móvil (overlay)
const MobileSearchOverlay = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { t } = useTranslate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults([]);
    }
  }, [isOpen]);

  const searchProducts = async (searchQuery: string) => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setResults([]);
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiManager.search.autocomplete(searchQuery, 5);
      let nextResults = response.success && response.data ? response.data : [];
      const exactProduct = await lookupExactProduct(searchQuery, nextResults);
      if (exactProduct) {
        const exactId = getProductId(exactProduct);
        nextResults = [
          exactProduct,
          ...nextResults.filter(product => getProductId(product) !== exactId)
        ];
      } else {
        nextResults = prioritizeExactRefMatch(nextResults, searchQuery);
      }
      setResults(nextResults);
    } catch (error) {
      console.error('Error al buscar productos:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (query.trim().length >= 2) {
        searchProducts(query);
      } else {
        setResults([]);
      }
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [query]);

  const handleSubmit = async (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault();
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;

    if (isReferenceLikeQuery(trimmedQuery)) {
      try {
        const res = await apiManager.products.lookupByRef(trimmedQuery);
        if (res.success && res.data?.id) {
          const skuParam = res.data.variantSku ? `?sku=${encodeURIComponent(res.data.variantSku)}` : '';
          onClose();
          navigate(`/products/${res.data.id}${skuParam}`);
          return;
        }
      } catch {
        // fallback to local exact match or normal search
      }
    }

    const exactProduct = await lookupExactProduct(trimmedQuery, results);
    onClose();
    if (exactProduct) {
      navigate(`/products/${getProductId(exactProduct)}`);
      return;
    }

    navigate(`/products?q=${encodeURIComponent(trimmedQuery)}`);
  };

  const handleResultClick = (productId: string) => {
    onClose();
    navigate(`/products/${productId}`);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/50" onClick={onClose}>
      <div
        className="bg-white w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 p-4" style={{ backgroundColor: '#f6cf02' }}>
          <button
            onClick={onClose}
            className="p-2 -ml-2"
            aria-label="Cerrar búsqueda"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="h-5 w-5">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <form onSubmit={handleSubmit} className="flex-1 flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('header.search_placeholder', 'Buscar...')}
              className="flex-1 bg-white border border-gray-200 rounded-full py-2 px-4 text-sm focus:outline-none focus:border-gray-400"
              style={{ color: '#0d0d0d' }}
            />
            <button
              type="submit"
              className="px-4 py-2 rounded-full text-sm font-medium transition-all"
              style={{ backgroundColor: '#0d0d0d', color: 'white' }}
              aria-label="Buscar"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="h-5 w-5">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
          </form>
        </div>

        {/* Resultados */}
        <div className="max-h-[70vh] overflow-y-auto bg-white">
          {isLoading ? (
            <div className="p-6 text-center text-gray-500">
              <div className="animate-spin h-6 w-6 border-2 border-black border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-2 text-sm"><T keyName="header.search_loading">Buscando...</T></p>
            </div>
          ) : query.trim().length >= 2 && results.length > 0 ? (
            <ul>
              {results.map((product) => (
                <li key={product.id} className="border-b border-gray-100">
                  <button
                    onClick={() => handleResultClick(product.id)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3"
                  >
                    {product.images && product.images[0] ? (
                      <OptimizedImage
                        src={product.images[0].url}
                        alt={product.name}
                        width={48}
                        height={48}
                        type="thumbnail"
                        className="h-12 w-12 object-contain rounded bg-gray-50"
                      />
                    ) : (
                      <div className="h-12 w-12 bg-gray-100 rounded flex items-center justify-center">
                        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                      {product.brand && <p className="text-xs text-gray-500 truncate">{product.brand}</p>}
                    </div>
                    {product.price && (
                      <div className="text-sm font-semibold text-gray-900">
                        {product.price.toFixed(2)}€
                      </div>
                    )}
                  </button>
                </li>
              ))}
              <li className="px-4 py-4 text-center border-t border-gray-200">
                <button
                  onClick={handleSubmit}
                  className="text-sm text-black hover:underline font-medium"
                >
                  <T keyName="header.search_view_all">Ver todos los resultados</T> para "{query}"
                </button>
              </li>
            </ul>
          ) : query.trim().length >= 2 ? (
            <div className="p-6 text-center text-gray-500">
              <p className="text-sm"><T keyName="header.search_no_results">No se encontraron productos</T></p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// Componente principal de Header
const Header = () => {
  const { auth, logout } = useAuth();
  const { cart } = useCart();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const operatorRole = auth.user?.role;
  const canAccessOperatorPanel =
    operatorRole === 'commercial' ||
    operatorRole === 'distributor' ||
    operatorRole === 'admin' ||
    operatorRole === 'beeadmin';

  return (
    <div className="sticky top-0 inset-x-0 z-50">
      {/* Overlay de búsqueda móvil */}
      <MobileSearchOverlay isOpen={isMobileSearchOpen} onClose={() => setIsMobileSearchOpen(false)} />

      <header className="relative h-16 mx-auto" style={{backgroundColor: '#f6cf02'}} data-testid="header">
        <nav className="flex items-center w-full h-full text-sm relative z-10 px-4 md:px-6" style={{color: '#0d0d0d'}} data-testid="nav-menu">

          {/* Sección izquierda: Menú + Búsqueda + Catálogo */}
          <div className="flex items-center gap-4 md:gap-6 flex-1">
            <SideMenu />
            {/* Icono de búsqueda móvil */}
            <button
              onClick={() => setIsMobileSearchOpen(true)}
              className="md:hidden p-1"
              aria-label="Buscar"
              data-testid="mobile-search-button"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </button>
            <div className="hidden md:block">
              <SearchBar />
            </div>
            <Link
              to="/catalog"
              className="hidden md:flex px-4 py-1.5 rounded-full text-sm font-medium transition-all border border-transparent hover:border-black"
              style={{
                backgroundColor: '#0d0d0d',
                color: 'white'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f6cf02';
                e.currentTarget.style.color = '#0d0d0d';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#0d0d0d';
                e.currentTarget.style.color = 'white';
              }}
              data-testid="catalog-link"
            >
              <T keyName="header.catalog">Catálogo</T>
            </Link>
            {auth.isAuthenticated && (
              <Link
                to="/quick-order"
                className="hidden md:flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all"
                style={{
                  color: 'white',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.15), 0 2px 4px rgba(0,0,0,0.3)'
                }}
                data-testid="quick-order-link"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span>Pedido rápido</span>
                <span className="ai-icon-reveal">
                  <span className="ai-badge">
                    <span>AI</span>
                    <span className="ai-star-top">✦</span>
                  </span>
                </span>
              </Link>
            )}
          </div>

          {/* Sección central: Logo */}
          <div className="flex items-center justify-center">
            <Link to="/" className="h-15" data-testid="logo-link">
              <OptimizedImage
                src="/Logo.svg"
                alt="Jarad"
                width={80}
                height={44}
                className="w-auto h-15"
                lazy={false}
              />
            </Link>
          </div>

          {/* Sección derecha: Acceso Privado + Carrito */}
          <div className="flex items-center gap-6 flex-1 justify-end">

            {/* Acceso Privado */}
            <div className="relative">
              {auth.isAuthenticated ? (
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="sm:px-4 px-2 py-1.5 rounded-full text-sm font-medium transition-all flex items-center sm:gap-2 gap-1 border border-transparent hover:border-black"
                    style={{backgroundColor: '#0d0d0d', color: 'white'}}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f6cf02';
                      e.currentTarget.style.color = '#0d0d0d';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#0d0d0d';
                      e.currentTarget.style.color = 'white';
                    }}
                    data-testid="user-menu-button"
                  >
                    <span className="hidden sm:inline">{auth.user?.name.split(' ')[0]}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      className="h-4 w-4 sm:hidden"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      className={`h-3 w-3 transition-transform duration-200 hidden sm:block ${isUserMenuOpen ? 'rotate-180' : ''}`}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Menú desplegable */}
                  {isUserMenuOpen && (
                    <div className="absolute top-full right-0 mt-1 bg-white shadow-lg rounded-md w-48 overflow-hidden z-50" data-testid="user-menu">
                      <div className="p-3 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">{auth.user?.name}</p>
                        <p className="text-xs text-gray-500">{auth.user?.email}</p>
                      </div>
                      <div className="py-1">
                        <Link
                          to="/account"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                          data-testid="my-account-link"
                        >
                                                      <T keyName="header.my_account">Mi cuenta</T>
                        </Link>
                        <Link
                          to="/orders"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                          data-testid="my-orders-link"
                        >
                                                      <T keyName="header.my_orders">Mis pedidos</T>
                        </Link>
                        {(auth.user?.role === 'admin' || auth.user?.role === 'beeadmin') && (
                          <Link
                            to="/admin/dashboard"
                            className="block px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-gray-100"
                            onClick={() => setIsUserMenuOpen(false)}
                            data-testid="admin-panel-link"
                          >
                            <T keyName="header.admin_panel">Panel de administración</T>
                          </Link>
                        )}
                        {canAccessOperatorPanel && (
                          <Link
                            to={getOperatorBasePath(operatorRole)}
                            className="block px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-gray-100"
                            onClick={() => setIsUserMenuOpen(false)}
                            data-testid="commercial-panel-link"
                          >
                            {getOperatorPanelLabel(operatorRole)}
                          </Link>
                        )}
                        <button
                          onClick={() => {
                            logout();
                            setIsUserMenuOpen(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                          data-testid="logout-button"
                        >
                                                      <T keyName="header.logout">Cerrar sesión</T>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="sm:px-4 px-2 py-1.5 rounded-full text-sm font-medium transition-all border border-transparent hover:border-black flex items-center justify-center"
                  style={{backgroundColor: '#0d0d0d', color: 'white'}}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f6cf02';
                    e.currentTarget.style.color = '#0d0d0d';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#0d0d0d';
                    e.currentTarget.style.color = 'white';
                  }}
                  data-testid="login-link"
                >
                  <span className="hidden sm:inline"><T keyName="header.private_access">Acceso Privado</T></span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    className="h-4 w-4 sm:hidden"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </Link>
              )}
            </div>

            {/* Carrito */}
            <Link
              to="/cart"
              className="flex items-center gap-2 text-sm font-medium hover:opacity-70 transition-opacity relative"
              style={{color: '#0d0d0d'}}
              data-testid="cart-link"
            >
              <div className="relative">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  className="h-5 w-5"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {cart.totalItems > 0 && (
                  <span
                    className="absolute -top-2 -right-2 bg-black text-white text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center"
                    data-testid="cart-badge"
                  >
                    {cart.totalItems > 99 ? '99+' : cart.totalItems}
                  </span>
                )}
              </div>
              <span className="hidden sm:inline"><T keyName="header.cart">Carrito</T></span>
            </Link>
          </div>
        </nav>
      </header>
    </div>
  );
};

export default Header; 
